"use client";

import React from "react";
import { Check } from "lucide-react";

export interface BentoCardData {
    squadUnit: string;
    squadRole: string;
    title: string;
    description: string;
}

export interface MemorylayerBentoBlockProps {
    tag?: string;
    title?: string;
    description?: string;
    cards?: BentoCardData[];
}

export default function MemorylayerBentoBlock({
    tag = "System Engineering",
    title = "High-Tech Bento Architecture",
    description = "Kitebe worked as an integrated product squad, allocating specialized resources across database tuning, LLM fine-tuning, and UI development.",
    cards = [
        {
            squadUnit: "Squad Unit 01",
            squadRole: "AI Integration",
            title: "Model tuning & RAG parameters",
            description: "We analyzed baseline LLM context thresholds, crafted fine-tuned system prompt structures, set up pgvector retrieval criteria, and optimized token allocation budgets."
        },
        {
            squadUnit: "Squad Unit 02",
            squadRole: "UX Design",
            title: "Visual Customizer",
            description: "Mapped persona dashboard settings grids, designed responsive audio visualizers, and set up brand palettes."
        },
        {
            squadUnit: "Squad Unit 03",
            squadRole: "DevOps",
            title: "Database latency",
            description: "Setup pgvector indexing inside PostgreSQL, managed Redis enterprise session buffers, and configured FastAPI server setups."
        },
        {
            squadUnit: "Squad Unit 04",
            squadRole: "QA Operations",
            title: "Automated sandbox testing pipeline",
            description: "We created structured CI/CD routines that test chatbot execution. Every code merge fires automated testing sandboxes that run prompt vulnerability scans, checking model outputs against known jailbreak scripts."
        }
    ]
}: MemorylayerBentoBlockProps) {
    return (
        <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#0A0A0C] w-full" data-header-theme="light">
            <div className="container mx-auto">
                <div className="max-w-2xl mb-16">
                    {tag && (
                        <span className="text-xs font-mono text-purple-400 uppercase tracking-widest block mb-4">// {tag}</span>
                    )}
                    <h2 className="text-4xl md:text-5xl font-light text-white leading-tight font-sans">
                        {title}
                    </h2>
                    {description && (
                        <p className="text-zinc-500 text-sm mt-2 font-sans font-light">
                            {description}
                        </p>
                    )}
                </div>

                {/* Bento Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    
                    {/* Bento Card 1: AI Integration Engineer (Col Span 2) */}
                    {cards[0] && (
                        <div className="md:col-span-2 bg-[#121214] border border-zinc-800 rounded-2xl p-8 flex flex-col justify-between relative overflow-hidden group hover:border-purple-500/30 transition-all duration-500">
                            <div className="absolute top-0 right-0 w-48 h-48 bg-purple-500/5 rounded-full blur-[60px] pointer-events-none" />
                            
                            <div className="space-y-4">
                                <div className="inline-flex items-center gap-2">
                                    <span className="text-[10px] font-mono uppercase bg-purple-500/10 text-purple-300 border border-purple-500/20 px-2 py-0.5 rounded">{cards[0].squadUnit}</span>
                                    <span className="text-[10px] font-mono text-zinc-500 uppercase">{cards[0].squadRole}</span>
                                </div>
                                <h3 className="text-2xl font-normal text-white">{cards[0].title}</h3>
                                <p className="text-zinc-400 text-xs md:text-sm leading-relaxed max-w-lg font-light font-sans">
                                    {cards[0].description}
                                </p>
                            </div>

                            {/* Mini code compilation simulation */}
                            <div className="mt-8 bg-zinc-950 border border-zinc-850 rounded-lg p-4 font-mono text-[10px] text-zinc-400 space-y-1.5 shadow-inner">
                                <div className="text-purple-400">&gt; python tune_prompt_metrics.py --prompt=sherlock</div>
                                <div className="text-zinc-500">[System] Validating prompt blueprint structure...</div>
                                <div className="text-emerald-400 flex items-center gap-1">
                                    <Check className="w-3.5 h-3.5" /> [Success] Prompt compiled. VRAM tokens optimized (342 tokens baseline).
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Bento Card 2: Lead Designer (Col Span 1) */}
                    {cards[1] && (
                        <div className="bg-[#121214] border border-zinc-800 rounded-2xl p-8 flex flex-col justify-between relative overflow-hidden group hover:border-purple-500/30 transition-all duration-500">
                            <div className="space-y-4">
                                <div className="inline-flex items-center gap-2">
                                    <span className="text-[10px] font-mono uppercase bg-purple-500/10 text-purple-300 border border-purple-500/20 px-2 py-0.5 rounded">{cards[1].squadUnit}</span>
                                    <span className="text-[10px] font-mono text-zinc-500 uppercase">{cards[1].squadRole}</span>
                                </div>
                                <h3 className="text-2xl font-normal text-white">{cards[1].title}</h3>
                                <p className="text-zinc-400 text-xs leading-relaxed font-light font-sans">
                                    {cards[1].description}
                                </p>
                            </div>

                            {/* UI Palette preview mock */}
                            <div className="mt-6 flex gap-2">
                                <div className="w-8 h-8 rounded bg-[#0A0A0C] border border-zinc-800 flex items-center justify-center text-[8px] font-mono text-zinc-500">zinc</div>
                                <div className="w-8 h-8 rounded bg-purple-600 flex items-center justify-center text-[8px] font-mono text-white">purp</div>
                                <div className="w-8 h-8 rounded bg-indigo-500 flex items-center justify-center text-[8px] font-mono text-white">indg</div>
                                <div className="w-8 h-8 rounded bg-emerald-400 flex items-center justify-center text-[8px] font-mono text-black">emld</div>
                            </div>
                        </div>
                    )}

                    {/* Bento Card 3: Infrastructure Architect (Col Span 1) */}
                    {cards[2] && (
                        <div className="bg-[#121214] border border-zinc-800 rounded-2xl p-8 flex flex-col justify-between relative overflow-hidden group hover:border-purple-500/30 transition-all duration-500">
                            <div className="space-y-4">
                                <div className="inline-flex items-center gap-2">
                                    <span className="text-[10px] font-mono uppercase bg-purple-500/10 text-purple-300 border border-purple-500/20 px-2 py-0.5 rounded">{cards[2].squadUnit}</span>
                                    <span className="text-[10px] font-mono text-zinc-500 uppercase">{cards[2].squadRole}</span>
                                </div>
                                <h3 className="text-2xl font-normal text-white">{cards[2].title}</h3>
                                <p className="text-zinc-400 text-xs leading-relaxed font-light font-sans">
                                    {cards[2].description}
                                </p>
                            </div>

                            {/* Database telemetry progress bars */}
                            <div className="mt-6 space-y-2.5 font-mono text-[9px] text-zinc-500">
                                <div className="space-y-1">
                                    <div className="flex justify-between">
                                        <span>pgvector Query matching</span>
                                        <span className="text-emerald-400 font-bold">18ms</span>
                                    </div>
                                    <div className="w-full bg-zinc-950 h-1 rounded-full overflow-hidden">
                                        <div className="bg-emerald-400 h-full w-[85%]" />
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <div className="flex justify-between">
                                        <span>Redis Buffer query hit</span>
                                        <span className="text-purple-400 font-bold">4ms</span>
                                    </div>
                                    <div className="w-full bg-zinc-950 h-1 rounded-full overflow-hidden">
                                        <div className="bg-purple-400 h-full w-[95%]" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Bento Card 4: Automated CI/CD (Col Span 2) */}
                    {cards[3] && (
                        <div className="md:col-span-2 bg-[#121214] border border-zinc-800 rounded-2xl p-8 flex flex-col justify-between relative overflow-hidden group hover:border-purple-500/30 transition-all duration-500">
                            <div className="absolute top-0 right-0 w-48 h-48 bg-indigo-500/5 rounded-full blur-[60px] pointer-events-none" />
                            
                            <div className="space-y-4">
                                <div className="inline-flex items-center gap-2">
                                    <span className="text-[10px] font-mono uppercase bg-purple-500/10 text-purple-300 border border-purple-500/20 px-2 py-0.5 rounded">{cards[3].squadUnit}</span>
                                    <span className="text-[10px] font-mono text-zinc-500 uppercase">{cards[3].squadRole}</span>
                                </div>
                                <h3 className="text-2xl font-normal text-white">{cards[3].title}</h3>
                                <p className="text-zinc-400 text-xs md:text-sm leading-relaxed max-w-lg font-light font-sans">
                                    {cards[3].description}
                                </p>
                            </div>

                            {/* Git log preview */}
                            <div className="mt-8 bg-zinc-950 border border-zinc-850 rounded-lg p-4 font-mono text-[10px] text-zinc-500 space-y-1 shadow-inner">
                                <div className="flex justify-between">
                                    <span>[CI/CD Commit #e8f492a]</span>
                                    <span className="text-purple-400 font-bold">vulnerability checks</span>
                                </div>
                                <div className="text-zinc-600">Checking: 12 common system jailbreak attack vectors...</div>
                                <div className="text-emerald-400 font-bold">12/12 test parameters PASSED. System safe for deployment.</div>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </section>
    );
}
